import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, useId } from "react"

type Props = Omit<ComponentProps<"div">, "onChange"> &
  VariantProps<typeof quantitySelectorStyles> & {
    value: number
    onChange(value: number): void
    min?: number
    max?: number
    disabled?: boolean
    label?: string
  }

export function QuantitySelector({
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  disabled,
  label = "Quantity",
  ...props
}: Props) {
  const id = useId()
  const styles = quantitySelectorStyles({ disabled })

  function change(next: number) {
    if (!disabled && Number.isFinite(next)) onChange(Math.max(min, Math.min(max, Math.trunc(next))))
  }

  return (
    <div {...props} role="group" aria-label={label} className={cn(styles, props.className)}>
      <button
        type="button"
        className={quantityButtonStyles()}
        aria-label={`Decrease ${label}`}
        aria-controls={id}
        disabled={disabled || value <= min}
        onClick={() => change(value - 1)}
      >
        <span aria-hidden="true">−</span>
      </button>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        aria-label={label}
        className="w-[28px] min-w-0 bg-transparent text-center tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-blue [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        onChange={(event) => change(event.currentTarget.value === "" ? min : event.currentTarget.valueAsNumber)}
      />
      <button
        type="button"
        className={quantityButtonStyles()}
        aria-label={`Increase ${label}`}
        aria-controls={id}
        disabled={disabled || value >= max}
        onClick={() => change(value + 1)}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  )
}

const quantitySelectorStyles = tv({
  base: "w-fit inline-flex items-center gap-1 type-label-s text-blue",
  variants: { disabled: { true: "opacity-50" } },
})

const quantityButtonStyles = tv({
  base: "size-[24px] shrink-0 inline-flex items-center justify-center rounded-full border border-blue text-[16px] leading-none hover:bg-blue hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-blue",
})
